import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, User, Mail, Phone, MessageSquare, Building, AlertCircle } from 'lucide-react';
import { formatPhoneNumber, formatPhoneForCRM, isValidPhoneNumber } from '../utils/phoneFormatter';
import { PRODUCT_INTERESTS, FORM_SOURCES } from '../utils/hubspotForms';
import { submitToBlackOps, captureAttribution } from '../utils/leadCapture';
import TurnstileWidget from './TurnstileWidget';
import { useTurnstile } from '../hooks/useTurnstile';
import { TURNSTILE_REQUIRED_MESSAGE } from '../utils/turnstile';
import CountryCodeSelector from './CountryCodeSelector';

interface UnifiedContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  productType?: keyof typeof PRODUCT_INTERESTS;
  formSource?: keyof typeof FORM_SOURCES;
  title?: string;
  subtitle?: string;
}

const UnifiedContactModal: React.FC<UnifiedContactModalProps> = ({ 
  isOpen, 
  onClose, 
  productType = 'general',
  formSource = 'navbar_contact',
  title = "Get Your Custom Quote",
  subtitle = "Let's discuss your agricultural water solution needs"
}) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    phone: '',
    message: ''
  });
  const [countryCode, setCountryCode] = useState('+1');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const turnstile = useTurnstile();

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Validate required fields
    if (!formData.firstName.trim()) {
      setError('Please enter your first name');
      setIsSubmitting(false);
      return;
    }

    if (!formData.email.trim()) {
      setError('Please enter your email address');
      setIsSubmitting(false);
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      setIsSubmitting(false);
      return;
    }

    // Validate phone number if provided
    if (formData.phone && !isValidPhoneNumber(formData.phone)) {
      setPhoneError('Please enter a valid phone number');
      setIsSubmitting(false);
      return;
    }

    if (!turnstile.token) {
      setError(TURNSTILE_REQUIRED_MESSAGE);
      setIsSubmitting(false);
      return;
    }

    try {
      const firstname = formData.firstName.trim();
      const lastname = formData.lastName.trim();

      // Format phone number for CRM with country code
      const formattedPhone = formData.phone ? formatPhoneForCRM(countryCode, formData.phone) : '';

      const formType = productType === 'general' ? 'contact' as const : 'quote_request' as const;

      const payload = {
        form_type: formType,
        form_id: formSource,
        first_name: firstname,
        last_name: lastname,
        email: formData.email.trim(),
        company_name: formData.company.trim() || undefined,
        phone: formattedPhone || undefined,
        message: formData.message.trim() || undefined,
        product_interest: PRODUCT_INTERESTS[productType],
        source: FORM_SOURCES[formSource],
        turnstile_token: turnstile.token,
        ...captureAttribution(),
      };

      const result = await submitToBlackOps(payload);

      if (result.success) {
        setIsSuccess(true);

        // Reset form after short delay
        setTimeout(() => {
          setFormData({ firstName: '', lastName: '', company: '', email: '', phone: '', message: '' });
          setCountryCode('+1');
          setPhoneError('');
          setIsSuccess(false);
          onClose();
        }, 3000);
      } else {
        console.error('Lead capture error:', result.error);
        setError('Something went wrong, please try again or call us at (509) 901-7476.');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setError('Something went wrong, please try again or call us at (509) 901-7476.');
    }

    // Turnstile tokens are single-use — clear it so a second submission works.
    turnstile.reset();
    setIsSubmitting(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Clear errors when user starts typing
    if (error) setError('');
    
    if (name === 'phone') {
      // Format phone number as user types
      const formattedValue = formatPhoneNumber(value);
      setFormData({
        ...formData,
        [name]: formattedValue
      });
      
      // Clear phone error when user starts typing
      if (phoneError) {
        setPhoneError('');
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const PhoneSection = () => {
    const phoneNumber = "(509) 901-7476";
    
    if (isMobile) {
      return (
        <a 
          href="tel:+15099017476"
          className="text-green-600 font-bold text-xl hover:text-green-700 transition-colors"
        >
          <span>{phoneNumber}</span>
        </a>
      );
    }
    
    return (
      <div className="text-green-600 font-bold text-xl">
        <span>{phoneNumber}</span>
      </div>
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4 pt-20 pb-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 50 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 max-h-[calc(100vh-6rem)] overflow-y-auto my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Success State */}
            {isSuccess ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Send className="text-green-600" size={32} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Thank You!</h2>
                <p className="text-gray-600 mb-6">
                  Your request has been submitted successfully. Our team will contact you within 24 hours.
                </p>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-green-800 font-medium">Need immediate assistance?</p>
                  <PhoneSection />
                </div>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="bg-gradient-to-r from-ctc-blue to-ctc-blue-dark p-6 text-white relative">
                  <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
                  >
                    <X size={24} />
                  </button>
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                      <Send className="text-white" size={24} />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold">{title}</h2>
                      <p className="text-white/90">{subtitle}</p>
                    </div>
                  </div>
                </div>

                {/* Prominent Phone Number Section */}
                <div className="bg-green-50 p-4 m-6 mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-green-800 mb-1">
                        Call Us Now
                      </h3>
                    </div>
                    <PhoneSection />
                  </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 pt-2 space-y-6">
                  {/* Error Message */}
                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                      <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                      <p className="text-red-700 text-sm">{error}</p>
                    </div>
                  )}

                  <div>
                    <label htmlFor="contact-firstName" className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type="text"
                        id="contact-firstName"
                        name="firstName"
                        required
                        value={formData.firstName}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors"
                        placeholder="Enter your first name"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="contact-lastName" className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type="text"
                        id="contact-lastName"
                        name="lastName"
                        required
                        value={formData.lastName}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors"
                        placeholder="Enter your last name"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="contact-company" className="block text-sm font-medium text-gray-700 mb-2">
                      Company Name
                    </label>
                    <div className="relative">
                      <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type="text"
                        id="contact-company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors"
                        placeholder="Enter your company name"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors"
                        placeholder="Enter your email address"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="contact-phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <div className="flex">
                      <CountryCodeSelector
                        value={countryCode}
                        onChange={setCountryCode}
                      />
                      <div className="relative flex-1">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <input
                          type="tel"
                          id="contact-phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className={`w-full pl-10 pr-4 py-3 border border-l-0 rounded-r-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors ${
                            phoneError ? 'border-red-300 bg-red-50' : 'border-gray-300'
                          }`}
                          placeholder="555 123 4567"
                        />
                      </div>
                    </div>
                    {phoneError && (
                      <p className="mt-1 text-sm text-red-600">{phoneError}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Select your country code and enter your phone number
                    </p>
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-gray-700 mb-2">
                      Project Details
                    </label>
                    <div className="relative">
                      <MessageSquare className="absolute left-3 top-3 text-gray-400" size={20} />
                      <textarea
                        id="contact-message"
                        name="message"
                        rows={4}
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your operation, acreage, crops, and specific needs..."
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors resize-none"
                      />
                    </div>
                  </div>

                  {/* HIDDEN: Product Interest - Data sent to HubSpot but not displayed to user */}
                  <input 
                    type="hidden" 
                    name="product_interest" 
                    value={PRODUCT_INTERESTS[productType]} 
                  />
                  <input 
                    type="hidden" 
                    name="form_source" 
                    value={FORM_SOURCES[formSource]} 
                  />

                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">What happens next?</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Our experts will review your requirements</li>
                      <li>• We'll contact you within 24 hours</li>
                      <li>• Schedule a consultation if needed</li>
                      <li>• Get personalized recommendations</li>
                    </ul>
                  </div>

                  <TurnstileWidget
                    ref={turnstile.ref}
                    onToken={turnstile.setToken}
                    action={formSource}
                  />

                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={onClose}
                      className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                    >
                      Cancel
                    </button>
                    <motion.button
                      type="submit"
                      disabled={isSubmitting || !formData.firstName.trim() || !formData.email.trim()}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="flex-1 bg-gradient-to-r from-ctc-blue to-ctc-blue-dark text-white px-4 py-3 rounded-lg font-medium hover:from-ctc-blue-dark hover:to-ctc-blue transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          <span>Sending...</span>
                        </>
                      ) : (
                        <>
                          <Send size={20} />
                          <span>Send Request</span>
                        </>
                      )}
                    </motion.button>
                  </div>

                  <p className="text-xs text-gray-500 text-center">
                    By submitting, you agree to receive updates about our products. 
                    You can unsubscribe at any time.
                  </p>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default UnifiedContactModal;